import { Amplify } from 'aws-amplify';
import {
	fetchAuthSession,
	fetchUserAttributes,
	getCurrentUser,
	signIn,
	signInWithRedirect,
	signOut,
} from 'aws-amplify/auth';

type LegacyUser = {
	username: string;
	attributes: Record<string, string>;
	signInUserSession: {
		idToken: { jwtToken: string; payload: Record<string, unknown> };
		accessToken: { jwtToken: string; payload: Record<string, unknown> };
	};
};

const notAuthenticatedError = 'The user is not authenticated';

const buildLegacyUser = async (): Promise<LegacyUser> => {
	const [currentUser, session, attributes] = await Promise.all([
		getCurrentUser(),
		fetchAuthSession(),
		fetchUserAttributes().catch(() => ({}) as Record<string, string>),
	]);

	const idToken = session.tokens?.idToken;
	const accessToken = session.tokens?.accessToken;

	return {
		username: currentUser.username,
		attributes,
		signInUserSession: {
			idToken: {
				jwtToken: idToken?.toString?.() || '',
				payload: (idToken?.payload as Record<string, unknown>) || {},
			},
			accessToken: {
				jwtToken: accessToken?.toString?.() || '',
				payload: (accessToken?.payload as Record<string, unknown>) || {},
			},
		},
	};
};

export const Auth = {
	configure: (config: Record<string, unknown>) => {
		Amplify.configure(config);
	},
	signOut: async () => {
		await signOut();
	},
	signIn: async (username: string, password: string) => {
		return signIn({ username, password });
	},
	federatedSignIn: async ({
		customProvider,
	}: {
		customProvider?: string;
	} = {}) => {
		if (customProvider) {
			return signInWithRedirect({ provider: { custom: customProvider } });
		}
		return signInWithRedirect();
	},
	currentAuthenticatedUser: async () => {
		try {
			return await buildLegacyUser();
		} catch {
			throw notAuthenticatedError;
		}
	},
};
